import React, { useState } from 'react';
import api from '../../connector/api';
import Logo from '../../assets/main-logo.png';

export default function AbsenceRequestForm() {
  const [lang, setLang] = useState('en'); // 'en' | 'km'

  const [formData, setFormData] = useState({
    parent_name: '',
    student_name: '',
    grade_class: '',
    start_date: '',
    end_date: '',
    reason: '',
  });

  const [status, setStatus] = useState({ loading: false, success: null, error: null });

  // Translation dictionary
  const t = {
    en: {
      schoolName: 'Cambodia Adventist International School',
      portalBadge: 'Student Absence Request Portal',
      headerTitle: 'Submit Absence Request',
      headerSubtitle: 'Fill out the form below to notify the school administration regarding student absence.',
      parentLabel: 'Parent / Guardian Name',
      parentPlaceholder: 'e.g. Eleanor Vance',
      studentLabel: 'Student Name',
      studentPlaceholder: 'e.g. Leo Vance',
      gradeLabel: 'Grade / Class',
      gradePlaceholder: 'e.g. Grade 10-B',
      startDateLabel: 'Start Date',
      endDateLabel: 'End Date',
      reasonLabel: 'Reason for Absence',
      reasonPlaceholder: 'Provide a brief explanation...',
      submitBtn: 'Send Request',
      submittingBtn: 'Submitting...',
      successMsg: 'Absence request submitted successfully!',
      error429: 'Too many requests. Please wait a minute before trying again.',
      errorDefault: 'Validation failed. Please check your inputs.',
    },
    km: {
      schoolName: 'សាលាអន្តរជាតិ អាដវេនទីស កម្ពុជា',
      portalBadge: 'ប្រព័ន្ធស្នើសុំការឈប់សម្រាករបស់សិស្ស',
      headerTitle: 'ដាក់ពាក្យស្នើសុំច្បាប់ឈប់សម្រាក',
      headerSubtitle: 'សូមបំពេញព័ត៌មានខាងក្រោមដើម្បីជម្រាបជូនរដ្ឋបាលសាលាអំពីការអវត្តមានរបស់សិស្ស។',
      parentLabel: 'ឈ្មោះមាតាបិតា / អាណាព្យាបាល',
      parentPlaceholder: 'ឧ. សុខ ចាន់',
      studentLabel: 'ឈ្មោះសិស្ស',
      studentPlaceholder: 'ឧ. សុខ វិចិត្រ',
      gradeLabel: 'ថ្នាក់ / កម្រិត',
      gradePlaceholder: 'ឧ. ថ្នាក់ទី ១០-B',
      startDateLabel: 'ថ្ងៃចាប់ផ្តើម',
      endDateLabel: 'ថ្ងៃបញ្ចប់',
      reasonLabel: 'មូលហេតុនៃការអវត្តមាន',
      reasonPlaceholder: 'សូមបញ្ជាក់មូលហេតុសង្ខេប...',
      submitBtn: 'ផ្ញើពាក្យស្នើសុំ',
      submittingBtn: 'កំពុងផ្ញើ...',
      successMsg: 'ពាក្យស្នើសុំច្បាប់ឈប់សម្រាកត្រូវបានផ្ញើដោយជោគជ័យ!',
      error429: 'ការស្នើសុំច្រើនពេក។ សូមរង់ចាំមួយនាទីមុនពេលព្យាយាមម្តងទៀត។',
      errorDefault: 'ការផ្ទៀងផ្ទាត់បរាជ័យ។ សូមពិនិត្យមើលព័ត៌មានដែលអ្នកបានបញ្ចូល។',
    },
  }[lang];

  // Dynamic font class helper
  const fontClass = lang === 'km' ? 'font-dynamic-serif' : 'font-dynamic-title';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: null, error: null });

    try {
      await api.post('/absence-records/', formData);
      
      setStatus({ loading: false, success: t.successMsg, error: null });
      setFormData({
        parent_name: '',
        student_name: '',
        grade_class: '',
        start_date: '',
        end_date: '',
        reason: '',
      });
    } catch (err) {
      if (err.response?.status === 429) {
        setStatus({ loading: false, success: null, error: t.error429 });
      } else {
        const errorData = err.response?.data;
        const firstError = errorData ? Object.values(errorData)[0] : null;
        setStatus({ loading: false, success: null, error: Array.isArray(firstError) ? firstError[0] : t.errorDefault });
      }
    }
  };

  return (
    <div className={`min-h-screen bg-slate-50 p-4 sm:p-6 flex flex-col items-center justify-center ${fontClass}`}>
      
      {/* Container Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 relative">
        
        {/* Language Switcher Toggle */}
        <div className="absolute top-4 right-4 flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
          <button
            type="button"
            onClick={() => setLang('en')}
            className={`px-2.5 py-1 text-xs font-semibold font-dynamic-title rounded-md transition-all ${
              lang === 'en'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            EN
          </button>
          <button
            type="button"
            onClick={() => setLang('km')}
            className={`px-2.5 py-1 text-xs font-semibold font-dynamic-serif rounded-md transition-all ${
              lang === 'km'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            ខ្មែរ
          </button>
        </div>

        {/* School Header Section */}
        <div className="flex flex-col items-center text-center pb-6 mb-6 border-b border-slate-100 mt-2">
          <div 
            className="w-16 h-16 mb-3 rounded-full flex items-center justify-center overflow-hidden p-1 shadow-sm border"
            style={{ backgroundColor: '#00285510', borderColor: '#00285520' }}
          >
            <img 
              src={Logo} 
              alt="School Logo" 
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span className="hidden text-2xl">🏫</span>
          </div>

          <h1 
            className="text-base font-bold tracking-tight leading-snug"
            style={{ color: '#002855' }}
          >
            {t.schoolName}
          </h1>
          
          <div 
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 font-semibold text-xs rounded-full"
            style={{ backgroundColor: '#00285510', color: '#002855' }}
          >
            <span>📋</span> {t.portalBadge}
          </div>
        </div>

        {/* Page Subheader */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-800">{t.headerTitle}</h2>
          <p className="text-xs text-slate-500 mt-1">{t.headerSubtitle}</p>
        </div>

        {/* Status Alerts */}
        {status.success && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-xl flex items-center gap-2">
            <span>✓</span> {status.success}
          </div>
        )}
        {status.error && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
            <span>⚠️</span> {status.error}
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Parent Name */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.parentLabel}</label>
            <input
              type="text"
              name="parent_name"
              required
              value={formData.parent_name}
              onChange={handleChange}
              placeholder={t.parentPlaceholder}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 transition-all placeholder:text-slate-400"
            />
          </div>

          {/* Student Name & Grade Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.studentLabel}</label>
              <input
                type="text"
                name="student_name"
                required
                value={formData.student_name}
                onChange={handleChange}
                placeholder={t.studentPlaceholder}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 transition-all placeholder:text-slate-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.gradeLabel}</label>
              <input
                type="text"
                name="grade_class"
                required
                value={formData.grade_class}
                onChange={handleChange}
                placeholder={t.gradePlaceholder}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 transition-all placeholder:text-slate-400"
              />
            </div>
          </div>

          {/* Date Range Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.startDateLabel}</label>
              <input
                type="date"
                name="start_date"
                required
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.endDateLabel}</label>
              <input
                type="date"
                name="end_date"
                required
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 transition-all"
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.reasonLabel}</label>
            <textarea
              name="reason"
              rows="3"
              required
              value={formData.reason}
              onChange={handleChange}
              placeholder={t.reasonPlaceholder}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 transition-all placeholder:text-slate-400 resize-none"
            ></textarea>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.loading}
            style={{ backgroundColor: '#002855' }}
            className="w-full mt-2 py-3 px-4 hover:opacity-95 active:scale-[0.99] disabled:opacity-50 text-white text-sm font-semibold rounded-xl shadow-sm transition-all duration-150 flex items-center justify-center gap-2"
          >
            {status.loading ? (
              <span>{t.submittingBtn}</span>
            ) : (
              <span>{t.submitBtn}</span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}